import { findRow, formatPrice } from "@/data/pricing";
import { cooLoadedCostSentence } from "@/data/benchmarks";
/**
 * FAQ pairs that appear visibly inside an article body, mirrored here so the
 * route can emit matching FAQPage JSON-LD. Only add a slug when the questions
 * and answers are genuinely rendered on the page.
 */
export type PostFaq = { q: string; a: string };

const faqPrice = (name: string): string => formatPrice(findRow(name)) ?? "";

export const postFaqs: Record<string, PostFaq[]> = {
"working-partner-vs-silent-partner": [
  {
    "q": "Should a silent partner get the same share of profit?",
    "a": "On ownership, usually yes: profit after the working partner's role is paid is normally shared by shareholding. The difference is that the working partner's reward for the role comes first."
  },
  {
    "q": "What if we cannot agree on what the role is worth?",
    "a": "Use an outside reference point, such as what the business would pay a general manager on the open market, rather than either partner's view."
  },
  {
    "q": "Do we need a shareholders agreement for this?",
    "a": "It is the usual place for it, alongside decisions, funding and exit. Your lawyer drafts it. Agreeing the commercial terms first makes that work clearer."
  },
  {
    "q": "Can a working partner just take more drawings?",
    "a": "Drawings from a company have tax consequences and can be caught by Division 7A if they are not handled properly. Agree the principle as partners, then have your accountant set the method."
  }
],
  "fractional-coo-cost-australia": [
  {
    q: "Are the prices really the prices?",
    a: "Yes, plus GST, published on the packages page, and they change there first. Where a figure on this page and the packages page ever differ, the packages page is current.",
  },
  {
    q: "What's the minimum commitment?",
    a: "One quarter. Retainers are committed 90 days at a time and reviewed at each boundary, renew or exit.",
  },
  {
    q: "Why publish prices when no other consultancy does?",
    a: "Because pricing is literally Ajility's specialisation, and a pricing consultant who hides his prices is telling you something. See why we publish our prices for the longer answer.",
  },
  {
    q: "Is a fractional COO cheaper than a consultant?",
    a: "Different, rather than cheaper. Consultants price by the day and deliver recommendations; a fractional COO prices by the outcome and is accountable for delivery inside your leadership rhythm. See the comparison with consultants and coaches for more.",
  },
  {
    q: "What does the first conversation cost?",
    a: "Nothing. The fit call is free and thirty minutes, and the Operating Scan before it is free and six minutes.",
  },
    {
      q: "What's a typical fractional COO hourly rate?",
      a: `Some providers bill by the hour or day, but rates vary widely and are rarely published. Ajility does not sell hours or day rates; it prices named deliverables and monthly rhythms, with composed retainers for businesses under $15M ${faqPrice("Fortnightly rhythm base")}.`,
    },
  ],
  "eos-implementer-vs-fractional-coo": [
    {
      q: "Is an EOS Implementer the same as a fractional COO?",
      a: "No. An EOS Implementer facilitates a defined management system across a small number of full days each year and teaches the leadership team to run it. A fractional COO holds an operating seat for one to three days a week and is accountable for what happens between those sessions. The Implementer is accountable for the system, the operator is accountable for the result.",
    },
    {
      q: "Which one should a $10M founder-led business choose?",
      a: "It depends on the leadership team rather than the revenue. If there are capable leaders who already deliver what they commit to, a facilitator supplies the missing language and rhythm at a lower cost. If the founder is the only operator in the business, structure on its own will not be executed and an operating seat is the better purchase.",
    },
    {
      q: "Can you use both at the same time?",
      a: "Yes, and it works where the roles are written down. The Implementer owns the quarterly and annual sessions, the operator chairs the weekly cadence and carries delivery between them. The failure mode is two people quietly competing to define the operating model, so agree who decides what before either starts.",
    },
    {
      q: "What does each cost in Australia?",
      a: "Implementer rates are quoted on request and vary widely, so ask for a written quote covering the full first year of sessions. Fractional COO retainers commonly run $3,000 to $16,000 plus GST a month depending on days and scope. Most Ajility composed retainers for businesses under $15M start from $3,900 per month plus GST and entry points start at the Operating Diagnostic from $3,600 plus GST.",
    },
    {
      q: "Does Ajility deliver EOS?",
      a: "No. Ajility is not affiliated with, endorsed by or certified in EOS, and does not deliver the EOS process. The term Integrator is used because it names a real gap, but the work is a fractional operating seat rather than framework facilitation.",
    },
    {
      q: "Should I use a Scaling Up coach or a fractional COO?",
      a: "A framework coach teaches and facilitates a method; a fractional COO holds the operating seat and answers for results between sessions. The same test applies as with any framework: if your team already executes, a facilitator is cheaper; if not, you need an operator.",
    },
    {
      q: "Can we implement EOS ourselves from the Traction book instead?",
      a: "Self-implementing can work when the leadership team already delivers what it commits to. It breaks down when nobody holds commitments between quarterly sessions. Ajility is not affiliated with EOS; it works as an operator inside the weekly cadence.",
    },
    {
      q: "What are the alternatives to EOS for running a business?",
      a: "Common alternatives include Scaling Up, OKRs, the Four Disciplines of Execution and a tailored operating rhythm. Ajility builds a tailored rhythm, borrowing from lean and standard financial disciplines, and sits in the operating seat rather than facilitating.",
    },
    {
      q: "Do quarterly rocks actually work or is it just a fad?",
      a: "Quarterly priorities work when someone enforces them between sessions. They fail when commitments are set and nobody checks weekly, which is why some teams say the method did not stick. The rhythm matters more than the method.",
    },
  ],
  "fractional-coo-vs-consultant-vs-coach": [
    {
      q: "What is the difference between a fractional COO, a management consultant and a business coach?",
      a: "A consultant analyses a defined problem and recommends a course of action, then leaves. A coach develops the person leading the business. A fractional COO takes an operating seat and is accountable for the work being done. The same symptom, a founder carrying too much, can send a business to any of the three.",
    },
    {
      q: "Which one do I need if good decisions are made and nothing happens?",
      a: "That is the operator's problem to solve. Analysis and personal development both assume the execution layer works. When decisions are sound but delivery is uneven, what is missing is a weekly rhythm, named owners and someone holding the line between meetings.",
    },
    {
      q: "When is a consultant the better purchase?",
      a: "When the question is hard, technical and faced once: a pricing rebuild, a market entry, a systems selection, a restructure with legal and tax consequences. Those are bounded pieces of work with a clear end, and paying for an ongoing seat to answer them is poor value.",
    },
    {
      q: "When is a coach the better purchase?",
      a: "When the constraint is the founder rather than the business. If the structure is reasonable and the leadership team is capable, but the founder cannot delegate, cannot hold people to account or is close to burnout, coaching addresses the actual cause and costs a fraction of an operating seat.",
    },
    {
      q: "What do the three cost in Australia?",
      a: "Coaching commonly runs $1,000 to $3,500 plus GST a month. Consulting is priced by project and varies enormously with the firm. Fractional operating retainers commonly run $3,000 to $16,000 plus GST a month. Ajility publishes its full ladder on the packages page, starting at the Operating Diagnostic from $3,600 plus GST.",
    },
    {
      q: "What return should I expect from a fractional COO?",
      a: "Measure return against the cost of the gap, not the fee: founder hours freed, margin recovered on underpriced work, overhead removed and decisions made faster. Ajility will not promise a return figure; its diagnostic sizes the constraint before you commit to more.",
    },
    {
      q: "Advisory board or fractional COO: which helps more?",
      a: "An advisory board offers perspective a few times a year and holds no operating accountability. A fractional COO works in the weekly rhythm and answers for delivery. Many businesses benefit from both: advisers for direction, an operator for execution.",
    },
    {
      q: "Fractional COO or HR consultant for redesigning our structure?",
      a: "An HR consultant usually handles policy, compliance and people processes. A fractional COO designs structure around how the business makes money, ties each seat to operating targets and stays to run the rhythm that holds people to them.",
    },
    {
      q: "How is a Full Diagnostic different from hiring a management consultant?",
      a: `A consultant's review often ends at the report. Ajility's Full Diagnostic, ${faqPrice("Full Diagnostic and 90-Day Roadmap")}, ends with a 90-day roadmap with owners and dates, and the same person can then install it. The price is fixed and agreed up front.`,
    },
    {
      q: "How long does a fractional COO engagement usually last?",
      a: "Engagements commonly run six to eighteen months, but the right length is however long it takes your team to run the rhythm without help. Ajility commits one quarter at a time and decides at each 90-day boundary whether to continue, pause or close.",
    },
  ],
  "when-to-hire-a-coo": [
    {
      q: "When should a founder-led business hire a COO?",
      a: "When the operating work in the business genuinely amounts to a seat rather than a set of tasks: the founder is the escalation point for most decisions, quarterly priorities slip without anyone owning them, and the leadership team cannot run a week without the founder in the room. Revenue is a weak signal on its own.",
    },
    {
      q: "What revenue do you need before hiring a full-time COO?",
      a: "Most founder-led businesses cannot justify a full-time operator below roughly $15M to $20M in revenue, because there is not five days a week of COO-level work to give one. Below that, a fractional seat or a fixed-scope install usually gives the same structure without the salary.",
    },
    {
      q: "Should I promote someone internally instead?",
      a: "Often yes, if the person has already shown judgement under pressure and the business can give them support while they grow into the role. The common mistake is promoting your best delivery person into an operating seat with no coaching and no defined accountabilities, which usually costs you a good operator and a year.",
    },
    {
      q: "How do I know if I need a full-time or a fractional COO?",
      a: "Count the days. If the operating work is one to three days a week, fractional fits and costs less. If it is genuinely five days across a large team with daily executive load, hire permanently, and consider using a fractional operator for six months first to define the role precisely before you recruit into it.",
    },
    {
      q: "When is the answer not to hire anyone?",
      a: "Three situations. The business loses money at the unit level, in which case pricing and cost structure come first. The founder does not actually want to release control, in which case any appointment fails. Or the business is under $2M with a simple model, where systems and one good administrative hire return more than senior leadership.",
    },
    {
      q: "Can I just keep doing the COO job myself?",
      a: "Many founders can for a while, and under about $2M with a simple model it is often right. The test is time: if under a third of your week goes on work only you can do, holding the operating seat yourself is costing growth.",
    },
    {
      q: "Should I hire a second-in-charge or use a fractional COO first?",
      a: "A second-in-charge only works if the seat is defined. A role that cannot be described in five accountabilities and four numbers is not ready to recruit. Running the seat fractionally first makes the brief precise, whether you then recruit or promote.",
    },
    {
      q: "Should I hire a general manager so I can step back, or is that premature?",
      a: "Define the seat before you fill it. If you cannot describe the role in about five accountabilities and four numbers, a permanent GM will inherit ambiguity. Running the seat fractionally for six to nine months first tells you what the role actually is.",
    },
    {
      q: "Do I need a COO or just an operations coordinator?",
      a: "An operations coordinator takes tasks off the founder; a COO takes decisions and accountability. If the problem is admin volume, hire support. If decisions queue behind you and plans slip, more admin help will not fix it.",
    },
    {
      q: "Operations director vs COO: what's the difference?",
      a: "The titles overlap. An operations director usually leads delivery functions under a CEO or GM; a COO typically owns the whole operating system and sits beside the founder. What matters is the accountabilities written for the seat, not the title.",
    },
    {
      q: "What breaks when a company grows from 20 to 50 staff?",
      a: "Informal management breaks first. The founder can no longer see every job, roles start to overlap, meetings lose structure and numbers arrive too late. The fix is an accountability chart, a weekly leadership rhythm and a scorecard before the next hiring round.",
    },
  ],
  "a-plan-nobody-tracks-is-a-document": [
    {
      q: "Should I hire a strategic planning consultant or a fractional COO?",
      a: "A planning consultant helps write the plan. A fractional COO turns it into a budget, a forecast that gets updated and a monthly rhythm where someone answers for the variance. A plan nobody tracks is a document.",
    },
    {
      q: "Is it worth paying a consultant to write our strategic plan?",
      a: "Only if someone also installs the tracking. A plan without a budget, an updated forecast and a monthly rhythm where someone answers for the variance is a document. Pay for the execution layer, not the slides.",
    },
  ],
  "electrical-margins": [
    {
      q: "When does a trade or contracting business need a general manager?",
      a: "Usually when the owner is still quoting, scheduling and chasing debtors while trying to win work, and jobs close before anyone knows their margin. A GM or fractional operator who owns job costing and the weekly schedule frees the owner to sell.",
    },
    {
      q: "How should an electrical contractor report labour cost per job?",
      a: "Book hours to jobs daily at true cost, including on-costs, travel and non-productive time, and report hours against budget weekly rather than at month end. Labour is the biggest variable in electrical work, so it needs the shortest feedback loop.",
    },
    {
      q: "What gross margin should an electrical contractor be making?",
      a: "It varies with the mix of service, maintenance and project work, so a single benchmark misleads. More useful is whether each job holds its quoted margin; measure that first, then test your estimating assumptions against the results.",
    },
    {
      q: "What overhead percentage is normal for a contracting business?",
      a: "It depends on size and work type, so test it against your own margins rather than a benchmark. In the electrical case on this site, from Adam's earlier operating career, overhead fell from 11% to 6.6% of sales.",
    },
    {
      q: "How can a trades business improve estimating accuracy?",
      a: "Use one consistent estimating method, price from tested cost rates, and feed quote to actual results from finished jobs back into those rates. The estimate should become the live budget the job is managed against.",
    },
  ],
  "founder-bottleneck": [
    {
      q: "Should I spend the money on a COO or more salespeople?",
      a: "If sales are strong but delivery, margin or decisions are jamming, more salespeople will add load to a broken engine, so fix the operating constraint first. If operations run well and demand is the real limit, hire for sales.",
    },
    {
      q: "How long does it realistically take for an owner to step out of operations?",
      a: "Usually several quarters. The first 90 days install the rhythm, owners and scorecard; the following quarters prove the team can hold them. Ajility works in 90-day blocks reviewed at each boundary, so progress is visible early rather than assumed.",
    },
    {
      q: "I do everything myself in my business. What should I delegate first?",
      a: "Delegate the recurring decisions that do not need your judgement: routine approvals under a set dollar limit, scheduling, standard pricing within agreed bands and supplier follow-up. Write the limit down, name the owner and review the results weekly rather than approving each one.",
    },
    {
      q: "Who should an accountant recommend when the founder is the bottleneck?",
      a: "Recommend an operator who can take the operating load off the founder by installing decision rights, a leadership rhythm and clear ownership, rather than a coach who only advises. Ajility does this as a fractional COO.",
    },
    {
      q: "I can't take two weeks off without the business grinding to a halt. What should I fix first?",
      a: "Start with decision rights: write down who decides what, and the dollar thresholds above which it comes to you. Then run a weekly leadership meeting against a short scorecard so the team can operate for a fortnight without you.",
    },
    {
      q: "What would happen to my business if I was off sick for three months?",
      a: "If decisions would queue until you returned, the business is a function of one person and carries real key person risk. The fix is named owners, written decision limits and a leadership rhythm the team runs without you, built before it is tested.",
    },
  ],
  "fractional-vs-fulltime": [
    {
      q: "Is a fractional COO worth the money?",
      a: "It is worth it when the cost of the problem continuing, in founder time, stalled deals and invisible margin, is larger than the fee, and when you have real operating problems but not a full week of COO work. A fixed-price diagnostic tests it first.",
    },
    {
      q: "What are the pros and cons of a fractional COO?",
      a: "Pros: senior judgement sized to the work, no fixed overhead, a fast start and quarterly exit points. Cons: limited time in the business, no specialist depth outside the operator's experience, and a risk of dependency unless handover is a written deliverable.",
    },
    {
      q: "What does a fractional COO actually do in a small business?",
      a: "A fractional COO runs the operating side of the business part time: chairing the leadership meeting, holding people to their priorities, building the scorecard and P&L visibility, and fixing the processes that keep failing. The work is delivery, not a report.",
    },
    {
      q: "How many days a week does a fractional COO spend in your business?",
      a: "Most fractional COOs work one to three days a week in a client business. An Ajility retainer usually runs a full day each week, or each fortnight for smaller engagements, scoped to the size of the business and the service lines being led.",
    },
    {
      q: "Do fractional COOs take equity or performance fees?",
      a: "Some do. Blended structures combining a retainer, a performance fee tied to operational and financial measures, and equity are used where incentives are genuinely aligned. Ajility's published prices are fees plus GST, and any other structure is agreed in writing.",
    },
  ],
  "healthcare-integrator": [
    {
      q: "Will my leadership team accept a part-time COO?",
      a: "Usually, if the role is announced with clear decision rights and the operator works inside the leadership meeting rather than around it. Teams resist ambiguity more than outsiders, so Ajility names who decides what early and leaders know where they stand.",
    },
  ],
  "mining-90-days": [
    {
      q: "How do I know which of our contracts are losing money?",
      a: "Report gross margin by contract, not just a company P&L. Allocate labour, plant, subcontract and direct overhead to each contract, compare actuals with the rates you are paid, and review it at least monthly.",
    },
  ],
  "pl-visibility": [
    {
      q: "How do I calculate gross margin by customer?",
      a: "Take each customer's revenue, subtract the direct cost of what they bought, then load cost to serve such as freight, service time, rebates and credits. Rank the result; the biggest accounts are often the thinnest.",
    },
    {
      q: "Which of my customers are actually profitable?",
      a: "Only cost to serve tells you. Two customers of the same size can cost very different amounts once freight, service time, discounts and credits are counted. Rank customers by margin after those costs, not by revenue.",
    },
    {
      q: "How do I report gross margin by product in Xero?",
      a: "Use Xero tracking categories for product lines, code sales and cost of sales to them consistently, and review the tracked P&L monthly. Your bookkeeper sets up the coding; Ajility helps decide which cuts leadership actually needs.",
    },
    {
      q: "How should I review my P&L each month as a business owner?",
      a: "Compare actuals to budget and the last forecast, look at gross margin by line before overheads, ask which driver caused each material variance, and check cash separately. Leave with one or two actions and owners.",
    },
    {
      q: "Why don't my managers trust the numbers?",
      a: "Usually because definitions differ, data is entered late or inconsistently, and nobody owns accuracy. Agree one definition and source per number, assign an owner, and use the numbers in every meeting; trust follows use.",
    },
    {
      q: "What's a good gross margin for an Australian SME?",
      a: "It varies widely by industry, so compare against your own sector and your own history rather than a single figure. More useful than the total is margin by product, customer and job, which shows where to act.",
    },
  ],
  "right-people-right-seats": [
    {
      q: "Do I need more middle managers or a COO?",
      a: "Adding managers into an unclear structure adds cost and confusion. First define the seats, what each owns and the numbers each answers for. Then you will know whether you need more managers or clearer ones.",
    },
    {
      q: "Should our board replace the GM or support them with a fractional COO?",
      a: "Often support first. If the GM is capable but unclear on what they own, defining the seat, decision rights and scorecard usually fixes more than replacing the person. A fractional operator can install that structure alongside the GM, then step back.",
    },
    {
      q: "How do I know if someone is in the wrong seat?",
      a: "First check the seat: if outcomes, measures and decision rights are unclear, fix those. If the seat is clear and the person consistently misses its numbers or avoids its decisions, the fit is wrong and a different seat may suit them better.",
    },
    {
      q: "How should I deal with an underperforming manager?",
      a: "First check the seat: are the outcomes, numbers and decision rights written and agreed? If not, fix that and allow a defined period. If the seat is clear and results still miss, it is a performance matter; get employment advice before any formal step.",
    },
    {
      q: "How do I keep my team accountable without micromanaging?",
      a: "Agree outcomes and numbers in advance, make decision rights explicit, and review results on a fixed rhythm instead of checking the work as it happens. You inspect the scorecard weekly; the person owns how they get there.",
    },
    {
      q: "Employee engagement dropped after a period of fast growth. What should I fix first?",
      a: "Start with clarity: whether each person knows what they own, what good looks like and what they may decide. In one business Adam ran before founding Ajility, defined seats coincided with 83% staff engagement, though results vary by business.",
    },
  ],
  "strategy-execution-90-day-rhythm": [
    {
      q: "Should a founder-led business use OKRs?",
      a: "OKRs can work, but in a business of 15 to 150 staff a simpler form usually holds better: three to five quarterly priorities, one owner each, tracked weekly. The tracking rhythm matters more than the framework name.",
    },
    {
      q: "How do I tell if my problem is strategy or execution?",
      a: "If you know broadly what needs to happen and it is not happening, the problem is execution: owners, rhythm and follow-through. If you genuinely do not know which way to go, it is strategy. Most founder-led businesses have the first problem.",
    },
    {
      q: "OKRs or quarterly priorities: which suits an SME better?",
      a: "For most businesses under 150 staff, three to five quarterly priorities with one owner each are simpler to run than full OKRs. OKRs suit larger teams that need goals cascaded through several layers. Either works only if tracked weekly.",
    },
    {
      q: "What makes a good quarterly priority?",
      a: "It is specific, finishable in 90 days, owned by one person and judged by a simple done or not done test. If it cannot be finished in the quarter, it is a project to break down, not a priority.",
    },
    {
      q: "How do you make managers actually own priorities?",
      a: "Put one name against each priority, never a team, define what done looks like and the date, and review it in the same weekly meeting every week. Shared accountability is no accountability.",
    },
    {
      q: "How do I get my team to finish quarterly goals?",
      a: "Keep the list short, give each goal one owner, review progress in the weekly leadership meeting and deal with anything off track that week. Goals slip when nobody checks them until the end of the quarter.",
    },
  ],
  "the-6m-price-review": [
    {
      q: "How do I improve operations across a multi-store retail business?",
      a: "Standardise how each store runs, put the same weekly measures in front of every store manager, and compare stores on margin, labour cost and stock as well as sales. Pricing is often the biggest untested lever across a network.",
    },
  ],
  "the-six-minute-operating-scan": [
    {
      q: "Is a free business scan just a sales funnel?",
      a: "It leads to a conversation only if you choose one. Ajility's scan shows the result without an email and the score is yours. As a screening instrument, its honest use is telling you where to look next, with or without Ajility.",
    },
  ],
  "transport-118-percent": [
    {
      q: "What KPIs should a transport company track weekly?",
      a: "Revenue and margin by lane or customer, fleet utilisation, on-time delivery, cost per kilometre, driver hours against paid hours, and debtors. Measure margin where it is won or lost, at lane and job level.",
    },
    {
      q: "Should I offer profit share to my staff?",
      a: "It works when staff can see how their work moves profit and the pool is funded only from profit above a threshold. The transport case on this site describes a company-wide scheme, from Adam's earlier operating career.",
    },
  ],
  "hire-a-general-manager-or-a-fractional-one": [
    {
      "q": "Should I hire a general manager or a fractional one?",
      "a": "Define the seat first. If an operating rhythm already exists and there is a full week of GM work, recruit. If the system has to be built, the business cannot yet carry a senior salary, or you cannot write the role in five accountabilities and four numbers, run it fractionally for a quarter or two and recruit into a seat you can then describe."
    },
    {
      "q": "How do I find a GM who has run a business, not just managed one?",
      "a": "Ask what they changed about how money was made in their last role, ask for a decision that cost the business something and what they did next, and ask them to read your P&L and name where margin is leaking. Operators name the number; managers describe process."
    },
    {
      "q": "Does a trades business need a fractional general manager or a full-time one?",
      "a": "Most trades businesses under $15M need the commercial spine first: live job costing, a variation register, labour booked to jobs daily, a 13-week cash view and a weekly meeting where supervisors answer for their jobs. A fractional GM builds that in one or two quarters; whether a full-time GM follows depends on how much of a week the seat then needs."
    },
    {
      "q": "What does a fractional GM cost in Australia?",
      "a": "Ajility prices the Fractional GM seat as a monthly rhythm committed one quarter at a time, with the diagnostic that defines the seat priced as a fixed one-off. Every price is a from price based on the size of the business and how much of the work is on site, and all of them are published on the packages page."
    }
  ],
  "how-to-step-back-from-sales-in-your-own-business": [
    {
      "q": "How do I step back from sales in my own business?",
      "a": "In this order: write a one-page delegation schedule so pricing, discounts and quotes under a limit no longer come to you; split sales into finding, winning and keeping the work and hand over finding and keeping first; put a five-line weekly scorecard between you and the pipeline; then hire or promote into a seat you can describe. Allow two quarters."
    },
    {
      "q": "What should I delegate first if I do everything myself?",
      "a": "Decisions before tasks. Standard pricing within an agreed band, discounts up to a set percentage, quotes under a dollar limit, supplier reorders and scheduling, each with a named owner and a limit above which it returns to you. Review results weekly rather than approving each one."
    },
    {
      "q": "How long does it take an owner to step out of operations?",
      "a": "Two quarters is realistic and four is common. The first quarter installs the delegation schedule, scorecard and weekly rhythm; the second proves the team can hold them while you only review. Hiring someone and disappearing in a fortnight usually puts the owner back in the seat by month three."
    },
    {
      "q": "What should I measure in a six-person business?",
      "a": "Five lines, weekly, one owner each: quotes issued and win rate, average margin on quotes won, pipeline value by stage, work in progress against capacity, and debtor days. The same five lines work at sixty people; only the meeting they are read in changes."
    }
  ],
  "how-to-restructure-a-small-business-group-with-shared-owners": [
    {
      "q": "How do I restructure a small business group with shared owners?",
      "a": "Start with the operating questions, not the org chart: which functions are shared and who owns each across the group, how shared cost is allocated to each entity's P&L, what each business answers for on its own, and the one-page accountability chart by seat. Keep your accountant and lawyer on entity structure, tax and employment law; this is the operating side."
    },
    {
      "q": "Should we share finance and marketing across our businesses?",
      "a": "Usually share finance, payroll, HR administration, IT and the systems behind them, under one accountable owner measured on debtor days, month-end timing and a 13-week cash view for all entities. Keep anything a customer can tell apart, sales and delivery, per business. Marketing: share the function and tools, keep the message per brand. Charge shared cost back on a simple driver."
    },
    {
      "q": "What does top heavy mean in a small business group and what do I do about it?",
      "a": "More leadership than the group's numbers can carry, usually because each business built its own operations manager, admin lead and bookkeeper. List every role above the front line with its loaded cost and the number it answers for: a role with no number is overhead and a number with two roles against it is duplication."
    },
    {
      "q": "Do I need an independent review before making staff redundant in Australia?",
      "a": "It is strongly advisable. A genuine redundancy under the Fair Work Act turns on the job no longer being required because of operational change, consultation where an award or agreement requires it, and consideration of redeployment. An independent operating review documents why the structure changed, before the decision about any individual. The legal tests are your employment lawyer's territory."
    },
    {
      "q": "Who is responsible for what in a small business group?",
      "a": "One owner per seat, three to five accountabilities and two to four numbers each, drawn on one chart for the whole group 12 to 36 months out. Nobody reports to two people, and every shared function has exactly one name against it across the group."
    }
  ],
  "13-week-cash-flow-forecast": [
    {
      "q": "What is a 13-week cash flow forecast?",
      "a": "A rolling, week-by-week view of cash in and cash out for the next quarter: opening cash, receipts, payments, closing cash and a minimum cash line. Every Monday the oldest week drops off and a new one is added, so it never expires."
    },
    {
      "q": "Why do I have cash flow problems when the business is profitable?",
      "a": "The P&L records revenue when the work is done; the bank records cash when the customer pays. Debtors, supplier terms, BAS, super and wages sit in that gap, and a growing business funds it out of its own pocket. The forecast shows the gap before the overdraft does."
    },
    {
      "q": "My cash flow forecast shows a shortfall in six weeks. What should I do?",
      "a": "Act in the weeks before the low point: bring receipts forward, chase overdue debtors, stage discretionary spend, agree terms with key suppliers and talk to the bank or your accountant early. Weeks of warning give options that days do not."
    },
    {
      "q": "Who should own the cash flow forecast?",
      "a": "One named person, usually the bookkeeper or finance lead, with 30 minutes every Monday to update actuals and roll the forecast forward. Not the accountant, who sees the business after the fact, and not the owner, who stops updating it the first busy week."
    }
  ],
  "revenue-leakage": [
    {
      "q": "What is revenue leakage?",
      "a": "Money the business earned but never collected: work done and not billed, discounts nobody approved, credits nobody questioned, scope delivered beyond a fixed-price quote, and price lists that never caught up with costs. It is almost always worth more than a price rise because the customer already agreed to pay it."
    },
    {
      "q": "Are my prices too low, or is the money leaking after the quote?",
      "a": "Test the leak first. Line up list price against invoiced price for your top 20 customers, add up every credit note, compare quoted against actual hours on 30 fixed-price jobs, and match completed jobs to invoices. If the leak is 2 to 5 per cent of revenue, close it before you touch the price list."
    },
    {
      "q": "Why do my sales reps keep discounting?",
      "a": "Because nothing stops them and nothing makes them feel it. Set a discount authority with a percentage above which a second approval is needed in writing, and pay commission on margin or on revenue at a margin floor so the person giving the discount carries its cost."
    },
    {
      "q": "How do I set a minimum price or floor for quotes?",
      "a": "Build the floor from fully loaded cost plus the minimum margin the business will accept, put it into the quoting template or tool, and require approval for anything below it. The floor protects the business when quoting under pressure."
    }
  ],
  "commission-and-bonus-structure-design": [
    {
      "q": "How do I tell if our bonus scheme is actually working?",
      "a": "Compare what was paid with what the business earned over recent periods, check whether payouts moved with profit, and ask staff to name the measures. A scheme that pays in poor years, or that nobody can explain, is not working."
    },
    {
      "q": "Should commission be paid on revenue or on margin?",
      "a": "On gross margin, or on revenue at a margin floor so a deal under the floor earns nothing. Paying on the top line pays the same for a 40 per cent margin deal and a 10 per cent one, and the second may lose money after commission."
    },
    {
      "q": "Are commission caps a good idea?",
      "a": "Usually not on the measure you want more of. Cap the discounting authority instead, and let margin-based commission run; a cap on the right behaviour gives your best performer no reason to push past September."
    },
    {
      "q": "Do commissions and bonuses attract superannuation in Australia?",
      "a": "Yes for employees, so a scheme that forgets it is understated by roughly the superannuation guarantee rate from day one. Build it in, check payroll tax thresholds and the Fair Work rules on entitlements, and have your accountant sign off the mechanics before the team sees it."
    }
  ],

  "payday-super-cash-flow": [
    {
      "q": "When did Payday Super start in Australia?",
      "a": "Payday Super started on 1 July 2026. From that date employers must pay super on each payday, and it must reach the employee's fund within 7 business days, with some extended deadlines, such as for new employees. The last quarterly payment, for the quarter ending 30 June 2026, was due by 28 July 2026."
    },
    {
      "q": "Does Payday Super cost my business more?",
      "a": "Not in the amount of super. The Super Guarantee rate is the same. What changes is timing: super leaves your account every pay run instead of once a quarter, so the cash you used to hold for up to three months is gone. For many small businesses that is a one-off drop in available cash that has to be planned for."
    },
    {
      "q": "What happened to the Small Business Superannuation Clearing House?",
      "a": "The ATO's Small Business Superannuation Clearing House closed permanently on 1 July 2026. Businesses that used it now pay through their payroll software, their default fund's clearing service or a commercial clearing house. Check that your chosen option can meet the 7 business day deadline."
    },
    {
      "q": "What happens if super is paid late under Payday Super?",
      "a": "If the fund does not receive the contribution within the deadline, the Super Guarantee Charge can apply. The ATO has changed the penalty to 25 or 50 per cent of the unpaid charge, depending on prior penalties. Talk to your accountant or the ATO about your specific situation."
    },
    {
      "q": "How do I show Payday Super in a cash flow forecast?",
      "a": "Put super in the same week as each pay run, alongside wages and PAYG withholding, then check your lowest closing balance over the next 13 weeks. If that number fell, it is the real cash level your business now operates on."
    }
  ],
  "card-surcharge-ban-pricing": [
    {
      "q": "When did the card surcharge ban start in Australia?",
      "a": "The ban on surcharging designated eftpos, Mastercard and Visa debit, credit and prepaid cards took effect on 1 October 2026, following the Reserve Bank's review of retail payments. Domestic interchange caps were lowered from the same date."
    },
    {
      "q": "Can I still charge a card fee under a different name?",
      "a": "A charge that works like a surcharge under another label is likely to draw complaints and regulator attention. The safer course is to include the cost in the price you display. If you are unsure about a specific practice, ask your payment provider or a lawyer."
    },
    {
      "q": "How do I work out what the surcharge ban costs my business?",
      "a": "Divide last quarter's merchant fees by the card revenue they relate to, to get your effective rate. Multiply that by the share of revenue paid by card. Compare the result to your net margin to see how much of your profit the fee now takes."
    },
    {
      "q": "Should a small business raise prices after the surcharge ban?",
      "a": "Usually, yes, but selectively. Repricing by product and customer protects the lines with thin margins without pushing price-sensitive customers away. It is also a good time to remove old discounts and set a minimum margin for quotes."
    }
  ],
  "ai-adoption-small-business": [
    {
      "q": "Why won't my staff use the AI tools we bought?",
      "a": "Usually because the tool arrived before a specific job, the process around it did not change, and nobody owns whether it is used. Pick one frequent, repeatable process, remove unnecessary steps, put the tool inside the systems people already use, and name an owner with a measure."
    },
    {
      "q": "What should a small business automate first?",
      "a": "Start with a process that happens many times a week, follows the same steps most of the time and frustrates someone. Quoting, bill coding, job admin and weekly reporting are common first choices in Australian businesses between $3M and $30M."
    },
    {
      "q": "Do we need an AI consultant or a business process automation consultant?",
      "a": "If the problem is that nobody has mapped and changed the process, you need operational help first and tools second. A specialist AI developer is useful once the process is clear and the job for the tool is defined."
    },
    {
      "q": "Is Microsoft Copilot worth it for a small business?",
      "a": "It can be, if the team already works in Microsoft 365 and you give it specific jobs, such as drafting quotes from templates or summarising meetings. Licences without defined jobs and an owner rarely pay back, whichever product you choose."
    }
  ],
  "small-business-succession-planning": [
    {
      "q": "When should I start succession planning for my small business?",
      "a": "Three to five years before you want to sell or step back. That is how long it usually takes to reduce owner dependence, build a management team and produce a track record of results without the owner running the business day to day."
    },
    {
      "q": "Why does paying myself a market salary change my business valuation?",
      "a": "Buyers value a business on normalised earnings: profit after paying a market rate for the work the owner does. If you are paid less than that, the reported profit overstates what a new owner would earn, and the buyer will adjust it down before applying a multiple."
    },
    {
      "q": "What do buyers look for in a small business?",
      "a": "A business that runs without the owner, a management team with clear roles and measures, documented processes, clean monthly accounts and no heavy dependence on one customer or supplier."
    },
    {
      "q": "Who should I talk to about valuing or selling my business?",
      "a": "Your accountant for tax and structure, a qualified valuer for value, and a business broker or corporate adviser for the sale. An operations adviser helps before that, by making the business worth more and easier to sell."
    }
  ],
  "security-of-payment-act-subcontractors": [
    {
      "q": "What is the Security of Payment Act?",
      "a": "Each Australian state and territory has a construction Security of Payment Act that gives people who carry out construction work or supply materials a statutory right to progress payments and a fast adjudication process if they are not paid. NSW's is the Building and Construction Industry Security of Payment Act 1999, and Western Australia's is the Building and Construction Industry (Security of Payment) Act 2021."
    },
    {
      "q": "What happens if a builder does not send a payment schedule?",
      "a": "In many states, if a valid payment claim is not answered with a payment schedule within the time the Act allows, the claimed amount can become payable and you may be able to recover it or apply for adjudication. The rules and time limits differ by state, so check with your state regulator or a construction lawyer."
    },
    {
      "q": "How long does a builder have to respond to a payment claim?",
      "a": "It depends on the state and the contract. In NSW, for example, a payment schedule is generally due within the time set by the contract or 10 business days after the claim is served, whichever comes first. Other states set different periods."
    },
    {
      "q": "How can a subcontractor protect itself if a builder is in financial trouble?",
      "a": "Claim promptly and correctly under the Act, track every payment schedule against its deadline, record variations as they are instructed, set an exposure limit per builder and stop new work when it is passed. Get legal advice early if payments start slipping."
    }
  ],
};
